import React from "react";
import { StepProgress } from "../../common/dashboard/StepProgress";
import { DashboardLogoUploadField } from "../../common/dashboard/LogoUploadField";

// The shared form primitives that used to live in this file are promoted to
// src/components/common/dashboard/ (Phase A of the platform polish pass).
// This module re-exports them under their original names so the member
// workflow pages (and Events Step1EventDetails) compile unchanged, and keeps
// only the member-module-specific pieces: workflow steps and their display
// resolvers, navigation-state helpers, and thin member-flavored wrappers.
export {
  cpaGold,
  cpaGoldDark,
  fieldSx,
  choiceControlLabelSx,
  checkboxControlSx,
  radioControlSx,
  selectionChipSx as memberSelectionChipSx,
} from "../../common/dashboard/formStyles";
export { SectionShell } from "../../common/dashboard/SectionShell";
export type { SectionShellProps } from "../../common/dashboard/SectionShell";
export {
  FormTextField,
  FormCalendarDateField,
  FormSelectField,
  FormMultiSelectField,
  MultiOptionGroup,
} from "../../common/dashboard/FormFields";
export type {
  FormTextFieldProps,
  FormCalendarDateFieldProps,
  FormSelectFieldProps,
  FormMultiSelectFieldProps,
  MultiOptionGroupProps,
} from "../../common/dashboard/FormFields";
export { getLogoInputValue } from "../../common/dashboard/LogoUploadField";

export const updateMemberNavigationStorageKey = "cmp-update-member-id";

const updateMemberNavigationStorageMaxAgeMs = 60 * 60 * 1000;

export type UpdateMemberNavigationState = {
  memberId?: string;
};

export type MemberWorkflowStep = {
  title: string;
  description: string;
};

export const memberWorkflowSteps: MemberWorkflowStep[] = [
  {
    title: "Member Directory Information",
    description: "Information that will appear in the directories",
  },
  {
    title: "Member Contacts",
    description: "All member contact details",
  },
  {
    title: "Member Insights",
    description: "Valuable member details",
  },
  {
    title: "Membership Type & Billing",
    description: "Payment details",
  },
];

// The mocks label step 1 "Member Directory Information" only while step 1 is
// active; the steps 2-4 pages show "Member Information" (binding difference in
// the step 1/2 transcription spec). Step 4's subtitle likewise reads "Payment
// details" everywhere except the step-4 page, where it reads "Billing
// details".
const getWorkflowStepTitle = (index: number, activeStep: number) =>
  index === 0 && activeStep !== 1
    ? "Member Information"
    : memberWorkflowSteps[index].title;

const getWorkflowStepDescription = (index: number, activeStep: number) =>
  index === memberWorkflowSteps.length - 1
    ? activeStep === 4
      ? "Billing details"
      : "Payment details"
    : memberWorkflowSteps[index].description;

export const getUpdateMemberNavigationState = (
  memberId: string
): UpdateMemberNavigationState => ({
  memberId,
});

export const getUpdateMemberNavigationMemberId = (state: unknown): string => {
  if (!state || typeof state !== "object") {
    return "";
  }

  const memberId = (state as { memberId?: unknown }).memberId;

  return typeof memberId === "string" ? memberId : "";
};

export const storeUpdateMemberNavigationMemberId = (
  memberId?: string | null
) => {
  if (!memberId || typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    updateMemberNavigationStorageKey,
    JSON.stringify({
      memberId,
      storedAtUtcMilli: Date.now(),
    })
  );
};

export const consumeUpdateMemberNavigationMemberId = (): string => {
  if (typeof window === "undefined") {
    return "";
  }

  const storedValue = window.sessionStorage.getItem(
    updateMemberNavigationStorageKey
  );

  if (!storedValue) {
    return "";
  }

  window.sessionStorage.removeItem(updateMemberNavigationStorageKey);

  try {
    const parsedValue = JSON.parse(storedValue) as {
      memberId?: unknown;
      storedAtUtcMilli?: unknown;
    };
    const memberId =
      typeof parsedValue.memberId === "string" ? parsedValue.memberId : "";
    const storedAtUtcMilli =
      typeof parsedValue.storedAtUtcMilli === "number"
        ? parsedValue.storedAtUtcMilli
        : 0;

    if (
      !memberId ||
      !storedAtUtcMilli ||
      Date.now() - storedAtUtcMilli > updateMemberNavigationStorageMaxAgeMs
    ) {
      return "";
    }

    return memberId;
  } catch (error) {
    return "";
  }
};

export type MemberStepProgressProps = {
  activeStep: number;
  ariaLabel?: string;
  onStepClick?: (step: number) => void;
};

export const MemberStepProgress = ({
  activeStep,
  ariaLabel = "Member steps",
  onStepClick,
}: MemberStepProgressProps) => (
  <StepProgress
    activeStep={activeStep}
    ariaLabel={ariaLabel}
    onStepClick={onStepClick}
    steps={memberWorkflowSteps}
    getStepTitle={getWorkflowStepTitle}
    getStepDescription={getWorkflowStepDescription}
    desktopGridColsClassName="lg:grid-cols-[1.2fr_1fr_1fr_1.2fr]"
    stepTestIdPrefix="member-step"
  />
);

export type LogoUploadFieldProps = {
  id: string;
  fileName: string;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  onRemove?: () => void;
  previewAlt?: string;
  previewUrl?: string;
};

// Spread order is load-bearing only if LogoUploadFieldProps ever grows a
// `heading` key: today the spread after `heading` cannot override it because
// the member props type has no such key. Keep `heading` first regardless.
export const LogoUploadField = (props: LogoUploadFieldProps) => (
  <DashboardLogoUploadField heading="Member Logo" {...props} />
);

// IDA "Coming Soon" affordance (MEM-CREATE-002/003): the mock pairs the gold
// IDA mascot mark with a solid red "Coming Soon" pill button. The pill is a
// non-functional placeholder until IDA ships.
export const IdaComingSoonAffordance = () => (
  <span className="inline-flex items-center gap-2">
    <span className="inline-flex items-center gap-1 text-sm font-extrabold text-cpaGold">
      <img
        src="/ida-chatbot-icon.png"
        alt="IDA mascot"
        className="h-[22px] w-[22px] object-contain"
      />
      IDA
    </span>
    <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1.5 text-[11px] font-bold uppercase leading-none tracking-wide text-white shadow-sm">
      Coming Soon
    </span>
  </span>
);
